import api from "services/api";
import { compactEstablishmentFilters } from "services/establishmentFilters";
import { parseListPayload } from "utils/paginationStats";

const authHeaders = () => {
  const token = localStorage.getItem("tk446655440000");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const value = (object, camel, pascal, fallback) =>
  object?.[camel] ?? object?.[pascal] ?? fallback;

export function normalizeEstablishment(row) {
  return {
    id: value(row, "id", "Id", null),
    name: value(row, "name", "Name", ""),
    code: value(row, "code", "Code", ""),
    type: value(row, "type", "Type", ""),
    status: value(row, "status", "Status", ""),
    creationDate: value(row, "creationDate", "CreationDate", ""),
    latitude: value(row, "latitude", "Latitude", ""),
    longitude: value(row, "longitude", "Longitude", ""),
    spId: value(row, "subPrefectureId", "SubPrefectureId", value(row, "spId", "SPId", "")),
    spName: value(row, "subPrefectureName", "SubPrefectureName", value(row, "spName", "SPName", "")),
    departmentId: value(row, "departmentId", "DepartmentId", ""),
    departmentName: value(row, "departmentName", "DepartmentName", ""),
    provinceId: value(row, "provinceId", "ProvinceId", ""),
    provinceName: value(row, "provinceName", "ProvinceName", ""),
    assignmentCount: Number(value(row, "assignmentCount", "AssignmentCount", 0)),
    attendanceCount: Number(value(row, "attendanceCount", "AttendanceCount", 0)),
    isActive: Boolean(value(row, "isActive", "IsActive", true)),
    createdAt: value(row, "createdAt", "CreatedAt", ""),
    updatedAt: value(row, "updatedAt", "UpdatedAt", ""),
    rowVersion: value(row, "rowVersion", "RowVersion", ""),
  };
}

export function buildEstablishmentListParams({
  pageNumber = 1,
  pageSize = 25,
  search = "",
  provinceId = "",
  departmentId = "",
  subPrefectureId = "",
  type = "",
  status = "",
} = {}) {
  return {
    pageNumber,
    pageSize,
    search: search || undefined,
    provinceId: provinceId || undefined,
    departmentId: departmentId || undefined,
    subPrefectureId: subPrefectureId || undefined,
    ...compactEstablishmentFilters({ type, status }),
    sortBy: "name",
    sortDirection: "asc",
  };
}

export async function getEstablishments({
  pageNumber = 1,
  pageSize = 25,
  search = "",
  provinceId = "",
  departmentId = "",
  subPrefectureId = "",
  type = "",
  status = "",
} = {}) {
  const { data } = await api.get("/web/establishments", {
    headers: authHeaders(),
    params: buildEstablishmentListParams({
      pageNumber,
      pageSize,
      search,
      provinceId,
      departmentId,
      subPrefectureId,
      type,
      status,
    }),
  });
  const items = value(data, "items", "Items", []);
  const pagination = value(data, "pagination", "Pagination", {});
  const totalRecords = Number(value(pagination, "totalRecords", "TotalRecords", items?.length || 0));
  return {
    items: (Array.isArray(items) ? items : []).map(normalizeEstablishment),
    pagination: {
      pageNumber: Number(value(pagination, "pageNumber", "PageNumber", pageNumber)),
      pageSize: Number(value(pagination, "pageSize", "PageSize", pageSize)),
      totalRecords,
      totalPages: Number(
        value(pagination, "totalPages", "TotalPages", Math.max(1, Math.ceil(totalRecords / pageSize)))
      ),
    },
  };
}

export function buildEstablishmentMapParams(filters = {}) {
  return {
    ...(filters.north != null ? { north: filters.north } : {}),
    ...(filters.south != null ? { south: filters.south } : {}),
    ...(filters.east != null ? { east: filters.east } : {}),
    ...(filters.west != null ? { west: filters.west } : {}),
    ...(filters.zoom != null ? { zoom: filters.zoom } : {}),
    provinceId: filters.provinceId || undefined,
    departmentId: filters.departmentId || undefined,
    subPrefectureId: filters.subPrefectureId || undefined,
    search: filters.search || undefined,
    ...compactEstablishmentFilters(filters),
  };
}

export function buildMapViewportGrid(bounds, rows = 4, columns = 4) {
  const north = Number(bounds?.north);
  const south = Number(bounds?.south);
  const east = Number(bounds?.east);
  const west = Number(bounds?.west);
  const rowCount = Math.max(1, Math.floor(Number(rows) || 1));
  const columnCount = Math.max(1, Math.floor(Number(columns) || 1));
  if (![north, south, east, west].every(Number.isFinite) || north <= south || east <= west) {
    return [];
  }

  const latitudeStep = (north - south) / rowCount;
  const longitudeStep = (east - west) / columnCount;
  const cells = [];
  for (let row = 0; row < rowCount; row += 1) {
    const cellNorth = north - row * latitudeStep;
    const cellSouth = row === rowCount - 1 ? south : north - (row + 1) * latitudeStep;
    for (let column = 0; column < columnCount; column += 1) {
      const cellWest = west + column * longitudeStep;
      const cellEast = column === columnCount - 1 ? east : west + (column + 1) * longitudeStep;
      cells.push({
        north: cellNorth,
        south: cellSouth,
        east: cellEast,
        west: cellWest,
        row,
        column,
      });
    }
  }
  return cells;
}

export function getProgressiveMapQueryZoom(
  visualZoom,
  rows = 4,
  columns = 4,
  minimumDetailZoom = 12
) {
  const safeVisualZoom = Number.isFinite(Number(visualZoom)) ? Number(visualZoom) : 0;
  const largestGridDimension = Math.max(1, Number(rows) || 1, Number(columns) || 1);
  const gridZoomOffset = Math.ceil(Math.log2(largestGridDimension));
  return Math.min(19, Math.max(Number(minimumDetailZoom) || 0, safeVisualZoom + gridZoomOffset));
}

export function parseEstablishmentMapResponse(data = {}) {
  const features = data?.features ?? data?.Features ?? [];
  const summary = data?.summary ?? data?.Summary ?? {};
  return {
    features: Array.isArray(features) ? features : [],
    summary: {
      totalInScope: Number(value(summary, "totalInScope", "TotalInScope", 0)),
      totalInViewport: Number(value(summary, "totalInViewport", "TotalInViewport", 0)),
      mappedInViewport: Number(value(summary, "mappedInViewport", "MappedInViewport", 0)),
      missingCoordinatesInViewport: Number(
        value(summary, "missingCoordinatesInViewport", "MissingCoordinatesInViewport", 0)
      ),
      returnedFeatures: Number(value(summary, "returnedFeatures", "ReturnedFeatures", 0)),
      clustered: Boolean(value(summary, "clustered", "Clustered", false)),
    },
  };
}

export function normalizeEstablishmentMapFeatures(features = []) {
  return (Array.isArray(features) ? features : []).reduce(
    (result, feature) => {
      const properties = feature?.properties ?? feature?.Properties ?? {};
      const row = { ...feature, ...properties };
      const coordinates =
        feature?.geometry?.coordinates ??
        feature?.Geometry?.Coordinates ??
        feature?.coordinates ??
        feature?.Coordinates ??
        [];
      const longitude = Number(
        coordinates[0] ??
          coordinates.longitude ??
          coordinates.Longitude ??
          row.longitude ??
          row.Longitude ??
          row.lng ??
          row.lon ??
          row.center?.longitude ??
          row.Center?.Longitude ??
          row.centerLongitude ??
          row.CenterLongitude
      );
      const latitude = Number(
        coordinates[1] ??
          coordinates.latitude ??
          coordinates.Latitude ??
          row.latitude ??
          row.Latitude ??
          row.lat ??
          row.center?.latitude ??
          row.Center?.Latitude ??
          row.centerLatitude ??
          row.CenterLatitude
      );
      if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return result;

      const featureKind = String(
        row.kind ?? row.Kind ?? row.featureType ?? row.FeatureType ?? row.markerType ?? ""
      ).toLowerCase();
      const clusterFlag =
        Boolean(row.cluster ?? row.Cluster ?? row.isCluster ?? row.IsCluster) ||
        featureKind.includes("cluster");
      const pointCount = Number(
        row.pointCount ??
          row.PointCount ??
          row.point_count ??
          row.establishmentCount ??
          row.EstablishmentCount ??
          row.count ??
          row.Count ??
          0
      );
      const isCluster = clusterFlag || pointCount > 1;
      if (isCluster) {
        result.clusters.push({
          id: row.clusterId ?? row.ClusterId ?? row.cluster_id ?? `${latitude},${longitude}`,
          latitude,
          longitude,
          count: Math.max(1, pointCount),
          expansionZoom: Number(row.expansionZoom ?? row.ExpansionZoom) || null,
        });
        return result;
      }

      const establishment = normalizeEstablishment({
        ...row,
        id: row.id ?? row.Id ?? row.establishmentId ?? row.EstablishmentId ?? row.estabId,
        name: row.name ?? row.Name ?? row.establishmentName ?? row.EstablishmentName,
        latitude,
        longitude,
      });
      if (establishment.id) result.establishments.push(establishment);
      return result;
    },
    { establishments: [], clusters: [] }
  );
}

export async function getEstablishmentMapViewport(filters = {}, signal) {
  const { data } = await api.get("/web/establishments/map", {
    headers: authHeaders(),
    params: buildEstablishmentMapParams(filters),
    signal,
  });
  return parseEstablishmentMapResponse(data);
}

export async function getEstablishmentsMap(filters = {}) {
  const { data } = await api.get("/web/establishments/map", {
    headers: authHeaders(),
    params: buildEstablishmentMapParams(filters),
  });
  const mapResponse = parseEstablishmentMapResponse(data);
  if (mapResponse.features.length) {
    return normalizeEstablishmentMapFeatures(mapResponse.features).establishments;
  }
  return parseListPayload(data).map(normalizeEstablishment);
}

export async function getAllEstablishments(filters = {}) {
  const pageSize = 100;
  const firstPage = await getEstablishments({ ...filters, pageNumber: 1, pageSize });
  const totalPages = firstPage.pagination.totalPages;
  if (totalPages <= 1) return firstPage.items;

  const remainingPages = await Promise.all(
    Array.from({ length: totalPages - 1 }, (_, index) =>
      getEstablishments({ ...filters, pageNumber: index + 2, pageSize })
    )
  );
  return [firstPage, ...remainingPages].flatMap((page) => page.items);
}

export async function getEstablishment(id) {
  const { data } = await api.get(`/web/establishments/${id}`, { headers: authHeaders() });
  return normalizeEstablishment(data);
}

export async function createEstablishment(payload) {
  const { data } = await api.post("/web/establishments", payload, { headers: authHeaders() });
  return normalizeEstablishment(data);
}

export async function updateEstablishment(id, payload) {
  const { data } = await api.put(`/web/establishments/${id}`, payload, { headers: authHeaders() });
  return normalizeEstablishment(data);
}

export async function getEstablishmentDeletionImpact(id) {
  const { data } = await api.get(`/web/establishments/${id}/deletion-impact`, {
    headers: authHeaders(),
  });
  return data;
}

export async function archiveEstablishment(id, payload) {
  const { data } = await api.delete(`/web/establishments/${id}`, {
    headers: authHeaders(),
    data: payload,
  });
  return data;
}

function normalizeLookup(data) {
  const items = value(data, "items", "Items", Array.isArray(data) ? data : []);
  return (Array.isArray(items) ? items : []).map((item) => ({
    id: value(item, "id", "Id", null),
    text: value(item, "name", "Name", ""),
  }));
}

export async function getWebProvinces() {
  const { data } = await api.get("/web/geography/provinces", { headers: authHeaders() });
  return normalizeLookup(data);
}

export async function getWebDepartments(provinceId) {
  const { data } = await api.get(`/web/geography/provinces/${provinceId}/departments`, {
    headers: authHeaders(),
  });
  return normalizeLookup(data);
}

export async function getWebSubPrefectures(departmentId) {
  const { data } = await api.get(`/web/geography/departments/${departmentId}/sub-prefectures`, {
    headers: authHeaders(),
  });
  return normalizeLookup(data);
}

export async function importEstablishments(file) {
  const formData = new FormData();
  formData.append("file", file);
  const { data } = await api.post("/web/establishments/import", formData, {
    headers: { ...authHeaders(), "Content-Type": "multipart/form-data" },
  });
  return data;
}
